import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  ServiceTicket,
  ServiceTicketActivity,
  ServiceTicketStatus,
  ServiceTicketPriority,
  ServiceTicketCategory,
} from "@/data/serviceTicketTypes";
import { toUiServiceTicket, toDbServiceTicketCreate } from "@/mappers/service-ticket.mapper";

// ── DB row shape — matches the Supabase service_tickets table ─────────────
export interface ServiceTicketRow {
  id: string;                        // uuid (primary key)
  ticket_id: string;                 // business key — SVC-001
  job_id: string | null;             // uuid FK to jobs.id
  customer_id: string | null;        // uuid (optional)
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  address: string;
  category: string;
  priority: string;
  status: string;
  subject: string;
  description: string | null;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  assigned_to: string | null;
  created_by: string | null;
  scheduled_date: string | null;
  scheduled_time_slot: string | null;
  activities: ServiceTicketActivity[];
  notes: string | null;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
}

// ── Resolves job_number → UUID and enriches tickets with job_number ────────
async function enrichWithJobNumbers(tickets: ServiceTicket[]): Promise<ServiceTicket[]> {
  const jobUuids = [...new Set(tickets.map((t) => t.jobId).filter(Boolean))];
  if (jobUuids.length === 0) return tickets;

  const { data: jobs } = await supabase
    .from("jobs")
    .select("id, job_number")
    .in("id", jobUuids);

  if (!jobs) return tickets;

  const byUuid = new Map<string, string>(
    (jobs as { id: string; job_number: string | null }[]).map((j) => [
      j.id,
      j.job_number ?? j.id,
    ])
  );

  return tickets.map((t) => ({
    ...t,
    jobId: byUuid.get(t.jobId) ?? t.jobId,
  }));
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Returns the next SVC-NNN ticket_id by querying the DB max. */
async function getNextTicketId(): Promise<string> {
  const { data } = await supabase
    .from("service_tickets")
    .select("ticket_id")
    .order("ticket_id", { ascending: false })
    .limit(1)
    .single();

  if (!data?.ticket_id) return "SVC-001";
  const match = String(data.ticket_id).match(/(\d+)$/);
  const next = match ? parseInt(match[1], 10) + 1 : 1;
  return `SVC-${String(next).padStart(3, "0")}`;
}

// ── Service ───────────────────────────────────────────────────────────────
export const ServiceTicketService = {
  getNextTicketId,

  async getAll(): Promise<{ data: ServiceTicket[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("service_tickets")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    const tickets = await enrichWithJobNumbers((data as ServiceTicketRow[]).map((r) => toUiServiceTicket(r)));
    return { data: tickets, error: null };
  },

  async getById(ticketId: string): Promise<{ data: ServiceTicket | null; error: unknown }> {
    const { data, error } = await supabase
      .from("service_tickets")
      .select("*")
      .eq("ticket_id", ticketId)
      .single();

    if (error || !data) return { data: null, error };
    const [enriched] = await enrichWithJobNumbers([toUiServiceTicket(data as ServiceTicketRow)]);
    return { data: enriched, error: null };
  },

  async getByJobNumber(jobNumber: string): Promise<{ data: ServiceTicket[] | null; error: unknown }> {
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .single();

    if (!jobRow) return { data: [], error: null };

    const { data, error } = await supabase
      .from("service_tickets")
      .select("*")
      .eq("job_id", jobRow.id)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    const tickets = (data as ServiceTicketRow[]).map((r) => toUiServiceTicket(r, jobNumber));
    return { data: tickets, error: null };
  },

  async getByStatus(status: ServiceTicketStatus): Promise<{ data: ServiceTicket[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("service_tickets")
      .select("*")
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error || !data) return { data: null, error };
    const tickets = await enrichWithJobNumbers((data as ServiceTicketRow[]).map((r) => toUiServiceTicket(r)));
    return { data: tickets, error: null };
  },

  async create(
    ticket: ServiceTicket
  ): Promise<{ data: ServiceTicket | null; error: unknown }> {
    // ticket.jobId is a job_number (e.g. "JOB-001") from the frontend.
    // Resolve to UUID since job_id is a UUID FK in the DB.
    let jobUuid: string | undefined;
    if (ticket.jobId) {
      const { data: jobRow } = await supabase
        .from("jobs")
        .select("id")
        .eq(jobColumnFor(ticket.jobId), ticket.jobId)
        .single();
      jobUuid = jobRow?.id;
    }

    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = { ...toDbServiceTicketCreate(ticket, jobUuid), organization_id: organizationId };

    const { data, error } = await supabase
      .from("service_tickets")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[ServiceTicketService.create] Supabase error:", JSON.stringify(error));
      return { data: null, error };
    }

    const [enriched] = await enrichWithJobNumbers([toUiServiceTicket(data as ServiceTicketRow)]);
    return { data: enriched, error: null };
  },

  async update(
    ticketId: string,
    updates: Partial<ServiceTicket>
  ): Promise<{ data: ServiceTicket | null; error: unknown }> {
    const { data: current, error: fetchError } = await supabase
      .from("service_tickets")
      .select("*")
      .eq("ticket_id", ticketId)
      .single();

    if (fetchError || !current) return { data: null, error: fetchError };

    const currentTicket = toUiServiceTicket(current as ServiceTicketRow);
    const merged = { ...currentTicket, ...updates };

    // Re-resolve job UUID if jobId was updated
    let jobUuid: string | undefined = (current as ServiceTicketRow).job_id ?? undefined;
    if (updates.jobId && updates.jobId !== currentTicket.jobId) {
      const { data: jobRow } = await supabase
        .from("jobs")
        .select("id")
        .eq(jobColumnFor(updates.jobId), updates.jobId)
        .single();
      jobUuid = jobRow?.id;
    }

    const patch = toDbServiceTicketCreate(merged, jobUuid);

    const { data, error } = await supabase
      .from("service_tickets")
      .update(patch)
      .eq("ticket_id", ticketId)
      .select()
      .single();

    if (error || !data) return { data: null, error };
    const [enriched] = await enrichWithJobNumbers([toUiServiceTicket(data as ServiceTicketRow)]);
    return { data: enriched, error: null };
  },

  async delete(ticketId: string): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("service_tickets")
      .delete()
      .eq("ticket_id", ticketId);
    return { error };
  },

  /** Append an activity entry to the ticket's activities array */
  async addActivity(
    ticketId: string,
    activity: ServiceTicketActivity
  ): Promise<{ data: ServiceTicket | null; error: unknown }> {
    const { data: current, error: fetchError } = await supabase
      .from("service_tickets")
      .select("activities")
      .eq("ticket_id", ticketId)
      .single();

    if (fetchError || !current) return { data: null, error: fetchError };

    const existingActivities: ServiceTicketActivity[] = Array.isArray(
      (current as { activities: ServiceTicketActivity[] }).activities
    )
      ? (current as { activities: ServiceTicketActivity[] }).activities
      : [];

    return ServiceTicketService.update(ticketId, {
      activities: [...existingActivities, activity],
    });
  },

  /** Bulk upsert — for seeding mock data */
  async bulkInsert(
    tickets: ServiceTicket[]
  ): Promise<{ count: number; error: unknown }> {
    // Resolve all job UUIDs up front
    const jobNumbers = [...new Set(tickets.map((t) => t.jobId).filter(Boolean))];
    let jobUuidMap = new Map<string, string>();

    if (jobNumbers.length > 0) {
      const { data: jobs } = await supabase
        .from("jobs")
        .select("id, job_number")
        .in("job_number", jobNumbers);

      if (jobs) {
        jobUuidMap = new Map(
          (jobs as { id: string; job_number: string }[]).map((j) => [j.job_number, j.id])
        );
      }
    }

    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const rows = tickets.map((t) => ({
      ...toDbServiceTicketCreate(t, jobUuidMap.get(t.jobId) ?? undefined),
      organization_id: organizationId,
    }));

    const { data, error } = await supabase
      .from("service_tickets")
      .upsert(rows, { onConflict: "ticket_id" })
      .select("id");

    return { count: data?.length ?? 0, error };
  },
};
